/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-02-16 08:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, Tabs, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { getFormValues, reset, FieldArray, change } from 'redux-form';
import Attribute from 'src/components/containers/merchandising-v2/rule/strategy/attributes/render-attributes';

import { NUMBER_CONDITION_PER_TYPE } from 'src/constants/merchandising-v2';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import SelectProduct from 'src/components/containers/merchandising-v2/rule/strategy/select-product';

type Props = {};

type ComposedProps = Props & WithI18nProps;

class AttributesModal extends Component<ComposedProps> {
  componentDidMount() {
    // Init value when open modal
    const { type, strategies } = this.props;
    this.initValueModal(type, strategies);
  }
  componentWillUpdate(nextProps, nextState) {
    // Init value when change type strategy
    const { type, strategies } = nextProps;
    this.initValueModal(type, strategies);
  }

  render() {
    const {
      i18n,
      type,
      strategies = [],
      strategyProducts,
      options,
      ruleType,
      appLanguage,
      selectedIndex,
      updateField,
      collections,
      renderPreview,
      parentRef,
      removeEmptyStrategies,
      strategyTab,
      onChangeTab
    } = this.props;

    const tabs = [
      {
        id: 'product',
        content: i18n.translate('AdminHTML.MerchandisingV2.Improvement.productTab'),
        panelID: 'by-product'
      },
      {
        id: 'attribute',
        content: i18n.translate('AdminHTML.MerchandisingV2.Improvement.attributeTab'),
        panelID: 'by-attribute'
      }
    ];
    const filterAttributeByType = strategies.filter(e => _.get(e, 'type', '') === type);
    const byAttributeTab = (
      <div style={{ marginTop: '15px' }}>
        <FieldArray
          name="strategies"
          component={Attribute}
          strategies={strategies}
          type={type}
          maxCondition={NUMBER_CONDITION_PER_TYPE[type]}
          ruleType={ruleType}
          options={options}
          appLanguage={appLanguage}
          updateField={updateField}
          collections={collections}
          selectedIndex={selectedIndex}
          renderPreview={renderPreview}
          parentRef={parentRef}
        />
        {filterAttributeByType.length < NUMBER_CONDITION_PER_TYPE[type] && (
          <div className="card-section card-add-field-attribute wrap-attribute">
            <div className="or-connector"></div>
            <div className="add-field-attribute">
              <Button onClick={() => this.handleAddStrategy()}>
                {i18n.translate('AdminHTML.MerchandisingV2.AttributesModal.addNew')}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
    const byProductTab = (
      <FieldArray
        name="strategies"
        component={SelectProduct}
        strategyProducts={strategyProducts}
        type={type}
        ruleType={ruleType}
        options={options}
        appLanguage={appLanguage}
        updateField={updateField}
        collections={collections}
        selectedIndex={selectedIndex}
        renderPreview={renderPreview}
        parentRef={parentRef}
      />
    );
    const tabComponents = [byProductTab, byAttributeTab];

    return (
      <>
        <div className="card-section">
          {type === 'hide' ? (
            <>
              <p className="default-text-style">
                {i18n.translate(`AdminHTML.MerchandisingV2.AttributesModal.hide_Description01`, {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        `AdminHTML.MerchandisingV2.AttributesModal.hide_Description01_t1`
                      )}
                    </TextStyle>
                  ),
                  t2: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        `AdminHTML.MerchandisingV2.AttributesModal.hide_Description01_t2`
                      )}
                    </TextStyle>
                  )
                })}
              </p>
              <p className="default-text-style">
                {i18n.translate(`AdminHTML.MerchandisingV2.AttributesModal.hide_Description02`)}
              </p>
            </>
          ) : (
            <p>{i18n.translate(`AdminHTML.MerchandisingV2.AttributesModal.${type}_Description`)}</p>
          )}
        </div>
        <Tabs
          tabs={tabs}
          selected={strategyTab}
          onSelect={index => {
            removeEmptyStrategies();
            onChangeTab(index);
          }}>
          <div className="strategy-content">{tabComponents[strategyTab]}</div>
        </Tabs>
      </>
    );
  }

  handleAddStrategy = (nextStrategies = null) => {
    const { strategies, updateField, type } = this.props;
    let clonedStrategies = _.cloneDeep(nextStrategies ? nextStrategies : strategies);

    clonedStrategies.push({
      type,
      attributes: []
    });

    // Update suggestion form
    updateField('strategies', clonedStrategies);
  };

  initValueModal = (type, strategies) => {
    if (strategies.length <= 0) {
      this.handleAddStrategy(strategies);
      return;
    }
    const firstType = _.get(strategies, '[0].type', null);
    const hasEmptyObject = strategies.find(e => _.isEmpty(e));

    if (!_.isEmpty(hasEmptyObject) || !hasEmptyObject) {
      const filterStrategyByType = strategies.filter(e => e.type === type);

      if (filterStrategyByType.length <= 0 && firstType) {
        this.handleAddStrategy(strategies);
      }
    }
  };
}

const mapStateToProps = (state, props) => {
  const { strategies } = props;
  const formValues = getFormValues('MerchandisingV2RuleForm')(state);
  return {
    formValues,
    strategies,
    options: _.get(state.filterData.productAttributes, 'optionsList', []),
    collections: _.get(state.filterData.productAttributes, 'collections', []),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: () => dispatch(reset('MerchandisingV2RuleForm')),
  // Update redux-form field
  updateField: (field, value) => dispatch(change(`MerchandisingV2RuleForm`, field, value))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(AttributesModal));
